import type { GameContext } from "./types.ts";
import type { MouseState } from "./inputHandlers.ts";
import {
  PHASE_BUILD,
  PHASE_COMBAT,
  PHASE_DRAFT,
  PHASE_GAME_OVER,
} from "./types.ts";
import {
  syncGold,
  tickWaveCountdown,
  setWaveCountdown,
  setWaveStartedThisCountdown,
  setSelectedTower,
} from "./stateIntents.ts";
import { CELL_SIZE, GRID_WIDTH, GRID_HEIGHT } from "../core/config.ts";
import { CellType } from "../core/types.ts";
import { FIREWALL_DEFAULTS } from "../entities/Firewall.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { localizedName, t } from "../utils/i18n.ts";
import type { ActiveSynergy } from "../entities/types.ts";
import type { TowerVisualEffects } from "../rendering/TowerRenderer.ts";
import { ELEMENTS } from "../entities/elements.ts";
import { glowCache } from "../rendering/GlowSpriteCache.ts";
import { canvasFont } from "../rendering/canvasType.ts";
import { BossPathColor } from "../rendering/BossPathColor.ts";
import { CorruptionLayer } from "../rendering/CorruptionLayer.ts";
import type { TowerActionController } from "./TowerActionController.ts";
import type { PayloadTargetMode } from "./cardEffectHandlers.ts";
import { createSimStep } from "./SimStep.ts";
import { RARITY_SLOT_COLORS } from "../core/palette.ts";
import { COLORS } from "../core/constants.ts";

/** F-20 (placement audit, perf): the ghost cost badge (`¢${cost}`) now renders
 *  on EVERY frame the ghost is on screen, including ineligible cells — the
 *  common state while hunting for a legal cell under drag-only touch
 *  placement, so this string was rebuilt far more often than before. Costs
 *  are a small closed set (tower/firewall configs), so a plain memo keyed by
 *  the numeric cost avoids the per-frame template-string allocation. */
const costBadgeCache = new Map<number, string>();
function costBadge(cost: number): string {
  let s = costBadgeCache.get(cost);
  if (s === undefined) {
    s = `¢${cost}`;
    costBadgeCache.set(cost, s);
  }
  return s;
}

/** Create the update and render functions for the game loop. */
export function createRenderPipeline(
  ctx: GameContext,
  mouse: MouseState,
  towerActions: TowerActionController,
): {
  update: (dt: number) => void;
  render: () => void;
  /** S126 — the ECHELON corruption layer; main.ts calls `beginRun` per run. */
  corruption: CorruptionLayer;
} {
  const {
    gs,
    grid,
    canvas,
    ctx: renderCtx,
    economy,
    combatSystem,
    waveManager,
    synergySystem,
    particleSystem,
    screenEffects,
    gridRenderer,
    entityRenderer,
    projectilePool,
    towerSelector,
    towerData,
    webglEffects,
    perfGovernor,
    draftSystem,
    hazardPoolRenderer,
  } = ctx;

  let frameStart = 0;
  let lastContextLostLog = 0;
  // S74-F02: dev-only perf toggle, tree-shaken in prod — StorageAdapter bypass accepted
  const perfEnabled =
    import.meta.env.DEV && window.localStorage.getItem("aiw_perf") === "1";
  const PERF_SAMPLE_FRAMES = 60;
  const perfAcc = {
    frames: 0,
    waveMs: 0,
    combatMs: 0,
    contagionMs: 0,
    clusterMs: 0,
    synergyMs: 0,
    scoreMs: 0,
    breachMs: 0,
    spawnDetectMs: 0,
    renderMs: 0,
    renderGridMs: 0,
    renderEnemiesMs: 0,
    renderTowersMs: 0,
    renderProjectilesMs: 0,
    renderParticlesMs: 0,
    renderEffectsMs: 0,
    renderWebglMs: 0,
    virusTowerUpdates: 0,
    enemies: 0,
    particles: 0,
  };
  combatSystem.setPerfEnabled(perfEnabled);

  if (perfEnabled) {
    const dpr = displayDpr();
    const logicalW = canvas.width / dpr;
    const logicalH = canvas.height / dpr;
    console.info("[perf] render pipeline", {
      dpr: Number(dpr.toFixed(2)),
      backingStore: `${canvas.width}x${canvas.height}`,
      logical: `${logicalW}x${logicalH}`,
    });
  }

  // Reused across frames — element keys are static, so we mutate booleans in
  // place instead of re-allocating an array + object via Object.fromEntries
  // every frame (P48-P1).
  const elementBoostedCache: Record<string, boolean> = {};
  for (const el of ELEMENTS) elementBoostedCache[el] = false;

  // BL-44 Part D — reused across frames: the per-frame TowerVisualEffects object
  // was re-allocated every render in the hot path. The setter only reads its
  // fields (never the reference), so we mutate this one in place instead (same
  // P48 allocation-hoist as elementBoostedCache). Determinism-neutral (render).
  const towerFx: TowerVisualEffects = {
    fireRateBoosted: false,
    rangeBoosted: false,
    damageBoosted: false,
    elementBoosted: elementBoostedCache,
    piercingActive: false,
    timeWarpActive: false,
    recursiveLoopActive: false,
  };

  function resetPerfAcc(): void {
    perfAcc.frames = 0;
    perfAcc.waveMs = 0;
    perfAcc.combatMs = 0;
    perfAcc.contagionMs = 0;
    perfAcc.clusterMs = 0;
    perfAcc.synergyMs = 0;
    perfAcc.scoreMs = 0;
    perfAcc.breachMs = 0;
    perfAcc.spawnDetectMs = 0;
    perfAcc.renderMs = 0;
    perfAcc.renderGridMs = 0;
    perfAcc.renderEnemiesMs = 0;
    perfAcc.renderTowersMs = 0;
    perfAcc.renderProjectilesMs = 0;
    perfAcc.renderParticlesMs = 0;
    perfAcc.renderEffectsMs = 0;
    perfAcc.renderWebglMs = 0;
    perfAcc.virusTowerUpdates = 0;
    perfAcc.enemies = 0;
    perfAcc.particles = 0;
  }

  function flushPerfSample(): void {
    if (!perfEnabled || perfAcc.frames === 0) return;
    const frames = perfAcc.frames;
    // BL-44: surface the governor's live ladder state so the owner can verify
    // the rungs drop in priority order on the real problem wave at DPR=2.
    const gov = perfGovernor.debug();
    console.table([
      {
        govLevel: gov.level,
        govRungs: gov.rungs,
        govWaveBias: gov.waveBias,
        govMotion: gov.motion,
        govParticleCap: gov.particleCap,
        govPostFx: gov.postFx,
      },
    ]);
    console.table([
      {
        sampleFrames: frames,
        avgWaveMs: Number((perfAcc.waveMs / frames).toFixed(3)),
        avgCombatMs: Number((perfAcc.combatMs / frames).toFixed(3)),
        avgContagionMs: Number((perfAcc.contagionMs / frames).toFixed(3)),
        avgClusterMs: Number((perfAcc.clusterMs / frames).toFixed(3)),
        avgSynergyMs: Number((perfAcc.synergyMs / frames).toFixed(3)),
        avgScoreMs: Number((perfAcc.scoreMs / frames).toFixed(3)),
        avgBreachMs: Number((perfAcc.breachMs / frames).toFixed(3)),
        avgSpawnDetectMs: Number((perfAcc.spawnDetectMs / frames).toFixed(3)),
        avgRenderMs: Number((perfAcc.renderMs / frames).toFixed(3)),
        avgRenderGridMs: Number((perfAcc.renderGridMs / frames).toFixed(3)),
        avgRenderEnemiesMs: Number(
          (perfAcc.renderEnemiesMs / frames).toFixed(3),
        ),
        avgRenderTowersMs: Number((perfAcc.renderTowersMs / frames).toFixed(3)),
        avgRenderProjectilesMs: Number(
          (perfAcc.renderProjectilesMs / frames).toFixed(3),
        ),
        avgRenderParticlesMs: Number(
          (perfAcc.renderParticlesMs / frames).toFixed(3),
        ),
        avgRenderEffectsMs: Number(
          (perfAcc.renderEffectsMs / frames).toFixed(3),
        ),
        avgRenderWebglMs: Number((perfAcc.renderWebglMs / frames).toFixed(3)),
        avgVirusTowerUpdates: Number(
          (perfAcc.virusTowerUpdates / frames).toFixed(2),
        ),
        avgEnemies: Number((perfAcc.enemies / frames).toFixed(1)),
        avgParticles: Number((perfAcc.particles / frames).toFixed(1)),
      },
    ]);
    resetPerfAcc();
  }
  // Render-only cosmetic clock (seconds). Advances every frame — including
  // during PHASE_DRAFT / PHASE_GAME_OVER when the sim clock is frozen — so
  // purely decorative shimmer (firewall pulse, hazard-pool glow) keeps moving
  // behind pause overlays. It accumulates dt (no wall clock), so it adds no
  // non-determinism and is never part of any sim snapshot. Sim-coupled visuals
  // (the stealth reveal transition, which diffs against enemy.revealedAt) MUST
  // stay on combatSystem.getSimTime() — do not migrate those onto renderClock.
  let renderClock = 0;

  // S126 / BL-112 — the ECHELON corruption (docs/design/ACT_III_CORRUPTION.md).
  // Render-only: it reads wave / comms tone / governor quality as snapshots and
  // draws on the render clock; it never touches sim state, gs.rng, or events.
  const corruptionLayer = new CorruptionLayer(grid);

  // S141 / BL-128 — the corruption CRESCENDO. A render-side, sim-blind per-wave
  // PRESSURE (0..1) reads only the existing telegraph events + the wave phase:
  // it SPIKES on a boss/elite/breach wave and EBBS to a floor in the breathers
  // and the build phase, so the red means something (no more constant wash).
  // The act ramp (D-224) is the spike ceiling; the smoothing here is the breath.
  let waveThreatTier = 0; // target pressure for the active wave's threat tier
  let corruptionPressure = 0; // smoothed (breathing) value driven on the render clock
  ctx.eventBus.on("boss.wave.incoming", () => {
    waveThreatTier = Math.max(waveThreatTier, 1.0); // her hand fully on the board
  });
  ctx.eventBus.on("wave.mutation.incoming", () => {
    waveThreatTier = Math.max(waveThreatTier, 0.85); // a mutated thread spikes
  });
  ctx.eventBus.on("breach.spawnActivating", () => {
    waveThreatTier = Math.min(1, Math.max(waveThreatTier, 0.6) + 0.2); // a flank
  });
  ctx.eventBus.on("wave.started", () => {
    waveThreatTier = Math.max(waveThreatTier, 0.5); // any live wave lifts off the floor
  });
  ctx.eventBus.on("wave.completed", () => {
    waveThreatTier = 0; // the lull — let her breathe back to the floor
  });

  // Boss path color: fades to red when boss is on grid, back to green when gone
  const bossPathColor = new BossPathColor();
  let bossCount = 0;
  ctx.eventBus.on("boss.spawned", () => {
    bossCount++;
    bossPathColor.activate();
  });
  // boss.killed and boss.defeated both fire for the same kill — only track one
  ctx.eventBus.on("boss.killed", () => {
    bossCount = Math.max(0, bossCount - 1);
    if (bossCount === 0) bossPathColor.deactivate();
  });
  // Also handle boss leaking (reachedEnd without being killed). BL-163 (S163):
  // key off `special.type` ONLY (no `maxHp >= 500` heuristic) so this decrement
  // is symmetric with the boss.spawned increment above — otherwise a leaking
  // HP≥500 anchor (e.g. cipher_breaker, which no longer increments bossCount)
  // would wrongly deactivate the boss path color while a real boss is still live.
  ctx.eventBus.on("enemy.reachedEnd", (...args: unknown[]) => {
    const { enemy } = args[0] as {
      enemy: { special?: { type?: string } };
    };
    const t = enemy.special?.type;
    const isBoss = t === "finalBoss" || t === "spawnAdds";
    if (isBoss) {
      bossCount = Math.max(0, bossCount - 1);
      if (bossCount === 0) bossPathColor.deactivate();
    }
  });
  gridRenderer.bossPathColor = bossPathColor;

  // CR3: Placement rejection visual feedback
  ctx.eventBus.on("placement.rejected", (...args: unknown[]) => {
    const data = args[0] as { col: number; row: number; reason: string };
    gridRenderer.showRejectFlash(data.col, data.row, data.reason);
  });

  combatSystem.enemies = waveManager.enemies;
  combatSystem.firewalls = gs.firewalls;
  const losBlockedAtBlockersOnly = (gx: number, gy: number): boolean => {
    const cell = grid.getCell(gx, gy);
    return cell?.type === CellType.OBSTACLE || cell?.type === CellType.BLOCKER;
  };
  combatSystem.losBlocked = losBlockedAtBlockersOnly;

  // BL-91: the master sim-update sequence now lives in ONE place (SimStep),
  // called from both the perf-timed and untimed paths below. The timing wrapper
  // (frame-budget check + sample flush) stays here; only the sequence moved.
  const simStep = createSimStep(ctx);

  function update(dt: number): void {
    const updateStart = perfEnabled ? performance.now() : 0;
    frameStart = performance.now();

    // Visual effects must always decay (even during draft/pause/gameover)
    screenEffects.update(dt);
    gridRenderer.mainframe.update(dt);
    gridRenderer.updateRejectFlash(dt);
    ctx.gameBanner.update(dt);
    ctx.floatingDmg.update(dt);
    particleSystem.update(dt);
    ctx.payloadDetonationFx.update(dt);
    bossPathColor.update(dt / 1000);
    renderClock += dt / 1000;
    // S126: the corruption advances on the render clock (it keeps breathing
    // behind draft/game-over overlays like the other cosmetic shimmer). The
    // optional-calls keep headless partial-mock contexts valid.
    // S141 / BL-128: smooth the pressure toward its target on the render clock
    // (~0.6 s) so the crescendo/decay BREATHES instead of stepping.
    corruptionPressure +=
      (waveThreatTier - corruptionPressure) * Math.min(1, dt / 600);
    corruptionLayer.update(
      dt,
      gs.wave,
      ctx.hud.rivalryTone?.(gs.lives) ?? "even",
      webglEffects.getQuality?.() ?? "high",
      corruptionPressure,
    );
    gridRenderer.setCorruptionIntensity?.(corruptionLayer.intensity);

    syncGold(gs, economy.currentGold);

    // Bug 7: the next-wave indicator (countdown + "PRESS SPACE — WAVE N") is
    // BUILD-only. Drive its visibility from the phase here instead of eagerly
    // revealing it on wave.completed (which fires BEFORE the draft is dismissed),
    // so it never shows under the draft overlay nor the breach cinematic. The
    // stored triggerWaveStart is reused (registered once at startup).
    if (gs.phase === PHASE_BUILD) ctx.waveIndicator.show(gs.wave + 1);
    else ctx.waveIndicator.hide();

    if (gs.phase === PHASE_DRAFT || gs.phase === PHASE_GAME_OVER) return;

    // CR-8: Wave countdown timer (waveCountdown is in milliseconds)
    if (gs.phase === PHASE_BUILD && gs.waveCountdown > 0) {
      tickWaveCountdown(gs, dt);
      const countdownSec = gs.waveCountdown / 1000;
      ctx.waveIndicator.setCountdown(countdownSec, gs.wave);
      // CR-11: Audio tick at T-3, T-2, T-1
      for (const sec of [3000, 2000, 1000]) {
        if (gs.waveCountdown <= sec && gs.waveCountdown + dt > sec) {
          ctx.eventBus.emit("countdown.tick", { remaining: sec / 1000 });
        }
      }
      // CR-6 (B7): Guard against auto-start re-firing after manual start
      if (gs.waveCountdown <= 0 && !gs.waveStartedThisCountdown) {
        setWaveStartedThisCountdown(gs, true);
        setWaveCountdown(gs, 0);
        ctx.waveIndicator.setCountdown(0, gs.wave);
        waveManager.startWave();
      }
    }

    // These two are scalar toggles that can change at runtime via skills/modules.
    combatSystem.firewallSlowActive = gs.firewallSlowActive;
    combatSystem.firewallSlowFactor = gs.firewallSlowFactor;

    // F2 / DD-14.7-5: spawn queue PAUSED during breach cinematic.
    // Existing enemies still move (via combatSystem.update below) — cinematic
    // is a pressure amplifier, not a freeze.
    // BL-91: ONE sim-update sequence, called from both paths. The perf path
    // passes the accumulator (sub-step timing happens inside SimStep) and owns
    // the outer frame counters + sample flush; the untimed path passes null.
    if (perfEnabled) {
      simStep.update(dt, perfAcc);

      perfAcc.frames++;
      perfAcc.enemies += waveManager.enemies.length;
      perfAcc.particles += particleSystem.activeCount;

      if (perfAcc.frames >= PERF_SAMPLE_FRAMES) {
        flushPerfSample();
      }
    } else {
      simStep.update(dt, null);
    }

    if (perfEnabled && updateStart > 0 && perfAcc.frames > 0) {
      const totalUpdateMs = performance.now() - updateStart;
      if (totalUpdateMs > 16.6) {
        console.debug(
          `[perf] frame update over budget: ${totalUpdateMs.toFixed(2)}ms`,
        );
      }
    }
  }

  function render(): void {
    const renderStart = perfEnabled ? performance.now() : 0;
    // Sim clock — feeds entityRenderer so the stealth reveal transition stays
    // coupled to enemy.revealedAt (set in sim time). Frozen during pause by
    // design; cosmetic-only shimmer uses renderClock instead (see above).
    const frameTime = combatSystem.getSimTime();

    // ALWAYS re-apply DPR transform at start of every frame.
    // After this transform, draw calls take *logical* coordinates; canvas.
    // {width,height} are *physical* (logical × dpr), so screen-space fillers
    // (clearRect, flash, vignette, banner) must be passed logical dims to
    // avoid drawing into a dpr²-scaled region (F-007: vignette 2× too thick,
    // banner text rendered off-canvas on HiDPI).
    const dpr = displayDpr();
    const logicalW = canvas.width / dpr;
    const logicalH = canvas.height / dpr;
    renderCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderCtx.globalAlpha = 1;

    renderCtx.clearRect(0, 0, logicalW, logicalH);
    screenEffects.applyTransform(renderCtx, logicalW, logicalH);

    const tRenderGrid = perfEnabled ? performance.now() : 0;
    gridRenderer.render();
    // S126: claimed cells sit ON the board surface, UNDER towers/enemies
    // (early-returns to zero draw calls through all of Act I).
    corruptionLayer.renderUnder(renderCtx);
    gridRenderer.renderRejectFlash();
    if (perfEnabled) {
      perfAcc.renderGridMs += performance.now() - tRenderGrid;
    }

    const tRenderEffects = perfEnabled ? performance.now() : 0;
    entityRenderer.setTime(frameTime);
    // CR-H5 (LD-22.1-4): render ALL registered paths from PathRegistry,
    // not just gs.pathPixels (primary). Without this, breach-activated
    // secondary paths are invisible to the player even though enemies
    // are spawning on them. Primary at full opacity with packets;
    // breach secondaries dimmer (0.55) and without packets (CR-P3 Bug D
    // — suppresses visual packet swarm in multi-path scenes).
    const allPaths = ctx.pathRegistry.getAllPixelPaths();
    for (let i = 0; i < allPaths.length; i++) {
      const isPrimary = i === 0; // registry returns primary first
      entityRenderer.renderPath(
        allPaths[i]!,
        isPrimary ? 1.0 : 0.55,
        isPrimary, // drawPackets only on primary
        bossPathColor,
      );
    }
    // CR2: Path reveal animation (new spawn → mainframe)
    ctx.pathRevealAnimation.render(renderCtx);
    renderSynergyLinks();
    renderGhostPreview();
    if (perfEnabled) {
      perfAcc.renderEffectsMs += performance.now() - tRenderEffects;
    }

    // Compute active skill visual effects for towers (mutate the hoisted object).
    for (const el of ELEMENTS) {
      elementBoostedCache[el] = draftSystem.getDamageMultiplier(el) > 1;
    }
    towerFx.fireRateBoosted = draftSystem.getGlobalFireRateMultiplier() > 1;
    towerFx.rangeBoosted = combatSystem.rangeMultiplier > 1;
    towerFx.damageBoosted = draftSystem.getGlobalDamageMultiplier() > 1;
    towerFx.piercingActive = combatSystem.piercingBonus > 0;
    towerFx.timeWarpActive = combatSystem.timeWarpActive;
    towerFx.recursiveLoopActive = gs.recursiveLoopThreshold > 0;
    entityRenderer.towerEffects = towerFx;
    entityRenderer.showAllHPBars = gs.showAllHPBars;

    const tRenderTowers = perfEnabled ? performance.now() : 0;
    // Task-009 (O-10, D-324): pass live enemies so a jammed tower can tether
    // back to its jamming source (parity with the Overseer's aura→host link).
    entityRenderer.renderTowers(gs.towers, waveManager.enemies);
    // Range display for selected tower (CR-11)
    if (gs.selectedTower) {
      const st = gs.selectedTower;
      renderCtx.strokeStyle = st.color;
      renderCtx.lineWidth = 2.2; // BL-300 — 0.66 -> 0.96 real px
      renderCtx.globalAlpha = 0.62;
      renderCtx.setLineDash([7, 5]);
      renderCtx.beginPath();
      renderCtx.arc(st.x, st.y, st.range, 0, Math.PI * 2);
      renderCtx.stroke();
      renderCtx.setLineDash([]);
      renderCtx.globalAlpha = 1;
    } else if (gs.hoveredTower) {
      // CR-28: Hover range indicator
      const ht = gs.hoveredTower;
      renderCtx.strokeStyle = ht.color;
      renderCtx.lineWidth = 1.8; // BL-300 — the quietest of the three, but visible
      renderCtx.globalAlpha = 0.34;
      renderCtx.beginPath();
      renderCtx.arc(ht.x, ht.y, ht.range, 0, Math.PI * 2);
      renderCtx.stroke();
      renderCtx.globalAlpha = 1;
    }
    // Cosmetic shimmer uses the render clock (keeps animating during pause).
    ctx.firewallRenderer.render(gs.firewalls, renderClock);
    hazardPoolRenderer.render(combatSystem.getHazardPools(), renderClock);
    if (perfEnabled) {
      perfAcc.renderTowersMs += performance.now() - tRenderTowers;
    }

    const tRenderEnemies = perfEnabled ? performance.now() : 0;
    entityRenderer.renderEnemies(waveManager.enemies);
    if (perfEnabled) {
      perfAcc.renderEnemiesMs += performance.now() - tRenderEnemies;
    }

    const tRenderProjectiles = perfEnabled ? performance.now() : 0;
    entityRenderer.renderProjectiles(projectilePool.active);
    if (perfEnabled) {
      perfAcc.renderProjectilesMs += performance.now() - tRenderProjectiles;
    }

    const tRenderParticles = perfEnabled ? performance.now() : 0;
    particleSystem.render();
    ctx.payloadDetonationFx.render(renderCtx);
    ctx.floatingDmg.draw(renderCtx);
    if (perfEnabled) {
      perfAcc.renderParticlesMs += performance.now() - tRenderParticles;
    }

    const tRenderScreenEffects = perfEnabled ? performance.now() : 0;
    // S126: her over-pass (veil / sigil / tears) sits on the composed scene but
    // UNDER the screen flash/vignette/banner, so outcome messaging stays legible
    // and the WebGL bloom (which reads the canvas after this) lights her magenta.
    corruptionLayer.renderOver(renderCtx, canvas, logicalW, logicalH, dpr);
    // BL-235 — persistent board-depth vignette (under the transient flash/
    // vignette + banner), so the arena edges read with depth.
    screenEffects.renderBoardVignette(renderCtx, logicalW, logicalH);
    screenEffects.renderFlash(renderCtx, logicalW, logicalH);
    screenEffects.renderVignette(renderCtx, logicalW, logicalH);
    ctx.gameBanner.render(renderCtx, logicalW, logicalH);
    if (perfEnabled) {
      perfAcc.renderEffectsMs += performance.now() - tRenderScreenEffects;
    }

    screenEffects.resetTransform(renderCtx, logicalW, logicalH);

    // Single render-time credit seam: all UI affordability in this frame is
    // derived from this snapshot (no cross-frame caching of credit state).
    const creditView = {
      currentGold: gs.gold,
      canAfford: (cost: number): boolean => gs.gold >= cost,
    };
    towerSelector.updateGold(creditView);

    // Tower info panel for selected tower
    if (gs.selectedTower) {
      // Find synergies this tower participates in
      const towerSynergies = synergySystem.activeSynergies
        .filter((syn) =>
          syn.towers.some(
            (t) =>
              t.col === gs.selectedTower!.col &&
              t.row === gs.selectedTower!.row,
          ),
        )
        // GA-18 (Spec C+): carry the DESCRIPTION through, not just the name.
        // All 12 synergies.json entries have a real description AND a full,
        // genuinely-translated EN+DE mirror (`synergy.<id>.desc`) — 24 strings
        // that no code path read, so the player never saw any of them. The
        // name alone ("Steam") says nothing about what the synergy does.
        .map((syn) => ({
          name: localizedName("synergy", syn.id, syn.name),
          desc: t(`synergy.${syn.id}.desc`, undefined, syn.description ?? ""),
        }));
      const tower = gs.selectedTower;
      const upgradeCost = tower.getDisplayUpgradeCost();
      const canAffordUpgrade =
        upgradeCost != null && creditView.canAfford(upgradeCost);
      towerSelector.showTowerInfo(gs.selectedTower, towerSynergies, {
        canAffordUpgrade,
        onUpgrade: () => towerActions.upgrade(tower),
        // CR-4: the panel's SELL now opens an INLINE confirm (no centered modal),
        // so onSell is the direct, already-confirmed sell transaction.
        onSell: () => towerActions.sell(tower),
        sellPrice: economy.getSellPrice(tower.totalInvested),
        // BL-205 — the panel closes itself on mouse-leave (after a genuine
        // mouseenter); clear the selection so this per-frame block does not
        // re-derive and reopen it on the next frame.
        onDismiss: () => setSelectedTower(gs, null),
      });
      towerSelector.refreshUpgradeAffordability(canAffordUpgrade, upgradeCost);
    } else {
      towerSelector.hideTowerInfo();
    }

    // WebGL2 post-processing — only if context is alive
    const tRenderWebgl = perfEnabled ? performance.now() : 0;
    if (!webglEffects.isContextLost()) {
      webglEffects.render(canvas, screenEffects.chromaBurst);
      // Double-check after render: context may have been lost mid-pipeline
      if (webglEffects.isContextLost()) {
        console.warn(
          "[Render] WebGL context lost mid-pipeline — skipping composite",
        );
      } else if (
        webglEffects.getQuality() !== "off" &&
        webglEffects.getQuality() !== "low"
      ) {
        const webglCanvas = webglEffects.getCanvas();
        renderCtx.save();
        renderCtx.setTransform(1, 0, 0, 1, 0, 0);
        // The post-FX canvas runs at a capped (logical) resolution to avoid
        // DPR² fill cost; upscale it to fill the full backing store here.
        // Bilinear smoothing on the soft bloom/chroma overlay is imperceptible.
        renderCtx.drawImage(webglCanvas, 0, 0, canvas.width, canvas.height);
        renderCtx.restore();
      }
    } else {
      const now = performance.now();
      if (now - lastContextLostLog > 5000) {
        console.debug(
          "[Render] WebGL context lost — rendering without post-processing",
        );
        lastContextLostLog = now;
      }
    }
    if (perfEnabled) {
      perfAcc.renderWebglMs += performance.now() - tRenderWebgl;
    }

    if (frameStart > 0) {
      // BL-44: the single load brain replaces the old WebGL-local degrade. It
      // fuses the measured frame-ms (reactive safety net) with the live
      // entity/particle counts (proactive) and drives the whole arena ladder.
      perfGovernor.sample(
        performance.now() - frameStart,
        waveManager.enemies.length,
        particleSystem.activeCount,
        waveManager.currentWave,
      );
    }

    if (perfEnabled && renderStart > 0) {
      perfAcc.renderMs += performance.now() - renderStart;
    }
  }

  /** Bug 13: the AIM-payload targeting reticle (in the payload's rarity colour),
   *  drawn at the hovered cell. `targetCell` highlights the whole cell + a small
   *  crosshair; `targetEnemy` draws a circular lock reticle (ring + ticks + X) so
   *  the two aim modes read distinctly. */
  function drawPayloadReticle(mode: PayloadTargetMode, color: string): void {
    const cx = mouse.col * CELL_SIZE + CELL_SIZE / 2;
    const cy = mouse.row * CELL_SIZE + CELL_SIZE / 2;
    renderCtx.save();
    renderCtx.strokeStyle = color;
    renderCtx.shadowColor = color;
    renderCtx.shadowBlur = 10;
    renderCtx.lineWidth = 2;

    if (mode === "targetCell") {
      renderCtx.globalAlpha = 0.85;
      renderCtx.setLineDash([5, 4]);
      renderCtx.strokeRect(
        mouse.col * CELL_SIZE + 3,
        mouse.row * CELL_SIZE + 3,
        CELL_SIZE - 6,
        CELL_SIZE - 6,
      );
      renderCtx.setLineDash([]);
      const arm = CELL_SIZE * 0.22;
      renderCtx.beginPath();
      renderCtx.moveTo(cx - arm, cy);
      renderCtx.lineTo(cx + arm, cy);
      renderCtx.moveTo(cx, cy - arm);
      renderCtx.lineTo(cx, cy + arm);
      renderCtx.stroke();
    } else {
      const r = CELL_SIZE * 0.42;
      renderCtx.globalAlpha = 0.9;
      renderCtx.beginPath();
      renderCtx.arc(cx, cy, r, 0, Math.PI * 2);
      renderCtx.stroke();
      const t0 = r * 0.78;
      const t1 = r * 1.14;
      renderCtx.beginPath();
      renderCtx.moveTo(cx, cy - t0);
      renderCtx.lineTo(cx, cy - t1);
      renderCtx.moveTo(cx, cy + t0);
      renderCtx.lineTo(cx, cy + t1);
      renderCtx.moveTo(cx - t0, cy);
      renderCtx.lineTo(cx - t1, cy);
      renderCtx.moveTo(cx + t0, cy);
      renderCtx.lineTo(cx + t1, cy);
      renderCtx.stroke();
      const d = r * 0.5;
      renderCtx.lineWidth = 3;
      renderCtx.beginPath();
      renderCtx.moveTo(cx - d, cy - d);
      renderCtx.lineTo(cx + d, cy + d);
      renderCtx.moveTo(cx + d, cy - d);
      renderCtx.lineTo(cx - d, cy + d);
      renderCtx.stroke();
    }

    renderCtx.shadowBlur = 0;
    renderCtx.globalAlpha = 1;
    renderCtx.restore();
  }

  // BL-75: a label beside the ghost (never under the pointer) + a non-color validity cue.
  // Static (no animation) → reduced-motion safe by construction (SPEC §12).
  function drawGhostBadge(
    col: number,
    row: number,
    text: string,
    color: string,
  ): void {
    renderCtx.save();
    renderCtx.font = canvasFont("ghostBadge");
    renderCtx.textBaseline = "middle";
    const padX = 5;
    const w = renderCtx.measureText(text).width + padX * 2;
    const h = 17;
    // Prefer to the RIGHT of the cell; flip LEFT near the right edge. Sits ABOVE the cell so
    // a touch finger (below the offset aim point) never covers it.
    let x = (col + 1) * CELL_SIZE + 5;
    if (x + w > GRID_WIDTH * CELL_SIZE - 2) x = col * CELL_SIZE - w - 5;
    if (x < 2) x = 2;
    const y = Math.max(2, row * CELL_SIZE - h - 2);
    renderCtx.globalAlpha = 1;
    renderCtx.fillStyle = "rgba(2,8,4,0.85)";
    renderCtx.fillRect(x, y, w, h);
    renderCtx.strokeStyle = color;
    renderCtx.lineWidth = 1;
    renderCtx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    renderCtx.fillStyle = color;
    renderCtx.textAlign = "left";
    renderCtx.fillText(text, x + padX, y + h / 2 + 0.5);
    renderCtx.restore();
  }

  function drawInvalidGlyph(cx: number, cy: number): void {
    // An ✕ so validity never reads by colour alone (a11y, SPEC §12).
    renderCtx.save();
    renderCtx.globalAlpha = 0.9;
    renderCtx.strokeStyle = "#ff6b6b";
    renderCtx.lineWidth = 2.5;
    renderCtx.lineCap = "round";
    const r = CELL_SIZE * 0.17;
    renderCtx.beginPath();
    renderCtx.moveTo(cx - r, cy - r);
    renderCtx.lineTo(cx + r, cy + r);
    renderCtx.moveTo(cx + r, cy - r);
    renderCtx.lineTo(cx - r, cy + r);
    renderCtx.stroke();
    renderCtx.restore();
  }

  function renderGhostPreview(): void {
    if (gs.phase !== PHASE_BUILD && gs.phase !== PHASE_COMBAT) return;
    // Bug 13: while aiming an AIM payload, the cursor reads as a targeting
    // reticle in the payload's rarity colour — never the stale tower ghost.
    if (gs.payloadTargetingMode && gs.payloadTargetingCard) {
      if (mouse.col >= 0 && mouse.row >= 0) {
        drawPayloadReticle(
          gs.payloadTargetingMode,
          RARITY_SLOT_COLORS[gs.payloadTargetingCard.rarity] ?? "#FFD700",
        );
      }
      return;
    }
    // PT-17 edit mode: a selected tower means "upgrade/sell" mode — suppress the
    // build-ghost so no ghost follows the cursor and a stray click can't place.
    if (gs.selectedTower != null) return;
    // F-20: only bail for a genuinely OFF-BOARD aim (no cell to draw at all). An
    // in-bounds but INELIGIBLE cell (occupied/blocked — `mouse.valid === false`)
    // used to return here too, so hovering/dragging over it gave ZERO feedback —
    // worse with drag-only-on-touch, where the player is actively hunting for a
    // legal cell with their finger. The branches below already render the correct
    // reject cue (✕ glyph + a red cost badge) whenever `canBuild` is false, which
    // covers ineligible cells too (they carry `sealsPath === false`, so they read
    // as the plain "can't build here" cue, not the path-sealing warning).
    if (
      mouse.col < 0 ||
      mouse.row < 0 ||
      mouse.col >= GRID_WIDTH ||
      mouse.row >= GRID_HEIGHT
    ) {
      return;
    }

    const cx = mouse.col * CELL_SIZE + CELL_SIZE / 2;
    const cy = mouse.row * CELL_SIZE + CELL_SIZE / 2;

    // Firewall ghost preview
    if (gs.placementMode === "firewall") {
      if (mouse.canBuild) {
        renderCtx.globalAlpha = 0.35;
        renderCtx.strokeStyle = COLORS.SENTINEL_GREEN;
        renderCtx.shadowColor = COLORS.SENTINEL_GREEN;
        renderCtx.shadowBlur = 8;
        renderCtx.lineWidth = 2;
        const half = CELL_SIZE * 0.4;
        renderCtx.strokeRect(cx - half, cy - half, half * 2, half * 2);
        renderCtx.shadowBlur = 0;
        renderCtx.globalAlpha = 1;
        drawGhostBadge(
          mouse.col,
          mouse.row,
          costBadge(FIREWALL_DEFAULTS.cost),
          COLORS.SENTINEL_GREEN,
        );
      } else {
        renderCtx.globalAlpha = 0.3;
        renderCtx.fillStyle = "#ff0000";
        renderCtx.fillRect(
          mouse.col * CELL_SIZE + 2,
          mouse.row * CELL_SIZE + 2,
          CELL_SIZE - 4,
          CELL_SIZE - 4,
        );
        renderCtx.globalAlpha = 1;
        drawInvalidGlyph(cx, cy);
        if (mouse.sealsPath) {
          drawGhostBadge(mouse.col, mouse.row, "SEALS PATH", "#FF4D4D");
        } else {
          drawGhostBadge(
            mouse.col,
            mouse.row,
            costBadge(FIREWALL_DEFAULTS.cost),
            "#FF6B6B",
          );
        }
      }
      return;
    }

    // Tower ghost preview — nothing selected → no ghost.
    const config =
      gs.selectedTowerType != null
        ? towerData[gs.selectedTowerType]
        : undefined;
    if (!config) return;

    if (mouse.canBuild) {
      // CR-P2 (Bug C): iterate ALL preview paths (primary + breach
      // secondaries) so player sees how every active route reshapes.
      // Primary at full amber (#ffaa00); breach secondaries dimmer.
      if (mouse.previewPaths && mouse.previewPaths.length > 0) {
        renderCtx.save();
        for (let pi = 0; pi < mouse.previewPaths.length; pi++) {
          const path = mouse.previewPaths[pi]!;
          if (path.length < 2) continue;
          const isPrimary = pi === 0;
          renderCtx.globalAlpha = isPrimary ? 0.5 : 0.3;
          renderCtx.strokeStyle = "#ffaa00";
          renderCtx.shadowColor = "#ffaa00";
          renderCtx.shadowBlur = isPrimary ? 6 : 3;
          renderCtx.lineWidth = isPrimary ? 2 : 1.5;
          renderCtx.setLineDash([6, 4]);
          renderCtx.beginPath();
          renderCtx.moveTo(path[0]!.x, path[0]!.y);
          for (let i = 1; i < path.length; i++) {
            renderCtx.lineTo(path[i]!.x, path[i]!.y);
          }
          renderCtx.stroke();
        }
        renderCtx.setLineDash([]);
        renderCtx.shadowBlur = 0;
        renderCtx.globalAlpha = 1;
        renderCtx.restore();
      }
      renderCtx.globalAlpha = 0.4;
      renderCtx.fillStyle = config.color;
      renderCtx.shadowColor = config.color;
      renderCtx.shadowBlur = 10;
      renderCtx.beginPath();
      renderCtx.arc(cx, cy, CELL_SIZE * 0.3, 0, Math.PI * 2);
      renderCtx.fill();
      renderCtx.shadowBlur = 0;
      // BL-300 (owner, build 99) — the PLACEMENT range ring was barely visible on
      // the device: it inherited the ghost's `globalAlpha = 0.4`, drew at
      // `lineWidth = 1` (0.44 REAL px at the 0.4367 stage scale — sub-pixel) and
      // used a `[4, 6]` dash, a 40 % duty cycle whose marks measure 1.75 real px.
      // It is the one read the player needs while choosing WHERE a tower goes, so
      // it now carries three signals instead of one: a faint coverage disc, a
      // proper 2.5 px ring at 0.85 alpha with a soft glow, and a denser dash.
      renderCtx.globalAlpha = 0.05;
      renderCtx.fillStyle = config.colorLight;
      renderCtx.beginPath();
      renderCtx.arc(cx, cy, config.range, 0, Math.PI * 2);
      renderCtx.fill();
      renderCtx.globalAlpha = 0.85;
      renderCtx.strokeStyle = config.colorLight;
      renderCtx.lineWidth = 2.5;
      renderCtx.shadowColor = config.colorLight;
      renderCtx.shadowBlur = 8;
      renderCtx.setLineDash([7, 5]);
      renderCtx.beginPath();
      renderCtx.arc(cx, cy, config.range, 0, Math.PI * 2);
      renderCtx.stroke();
      renderCtx.setLineDash([]);
      renderCtx.shadowBlur = 0;
      renderCtx.globalAlpha = 1;
      drawGhostBadge(mouse.col, mouse.row, costBadge(config.cost), config.colorLight);
    } else {
      renderCtx.globalAlpha = 0.3;
      renderCtx.fillStyle = "#ff0000";
      renderCtx.fillRect(
        mouse.col * CELL_SIZE + 2,
        mouse.row * CELL_SIZE + 2,
        CELL_SIZE - 4,
        CELL_SIZE - 4,
      );
      renderCtx.globalAlpha = 1;
      drawInvalidGlyph(cx, cy);
      if (mouse.sealsPath) {
        drawGhostBadge(mouse.col, mouse.row, "SEALS PATH", "#FF4D4D");
      } else {
        drawGhostBadge(mouse.col, mouse.row, costBadge(config.cost), "#FF6B6B");
      }
    }
  }

  function renderSynergyLinks(): void {
    const synergies = synergySystem.activeSynergies as readonly ActiveSynergy[];
    const now = Date.now() * 0.003;
    for (const syn of synergies) {
      const towers = syn.towers;
      const isTri = syn.elements.length >= 3;
      const isResonance = syn.id.includes("resonance");

      // CR-12: Hierarchy styling. F-13: animate dash offset so energy appears
      // to flow along the link (negative offset → flow toward the center node).
      const flow = -(now * 6) % 16;
      if (isTri) {
        renderCtx.lineWidth = 2.5;
        renderCtx.setLineDash([6, 3]);
        renderCtx.lineDashOffset = flow;
      } else if (isResonance) {
        renderCtx.lineWidth = 1;
        renderCtx.setLineDash([2, 4]);
        renderCtx.lineDashOffset = flow;
      } else {
        renderCtx.lineWidth = 1.5;
        renderCtx.setLineDash([3, 5]);
        renderCtx.lineDashOffset = flow;
      }

      renderCtx.strokeStyle = syn.color;
      renderCtx.globalAlpha = 0.5 + Math.sin(now) * 0.2;
      for (let j = 0; j < towers.length - 1; j++) {
        renderCtx.beginPath();
        renderCtx.moveTo(towers[j]!.x, towers[j]!.y);
        renderCtx.lineTo(towers[j + 1]!.x, towers[j + 1]!.y);
        renderCtx.stroke();
      }
      renderCtx.setLineDash([]);
      renderCtx.lineDashOffset = 0;

      // Center node glow
      glowCache.draw(
        renderCtx,
        syn.colorLight,
        4,
        syn.centerX,
        syn.centerY,
        0.7,
      );
      renderCtx.beginPath();
      renderCtx.arc(syn.centerX, syn.centerY, 4, 0, Math.PI * 2);
      renderCtx.fillStyle = syn.colorLight;
      renderCtx.fill();
      renderCtx.globalAlpha = 1;
    }
  }

  return { update, render, corruption: corruptionLayer };
}
